<template lang="pug">
.container
    MilSymbol(
        v-if='showSelection'
        :mil-symbol='milSymbolSelected'
        :size='100'
        outlineColor='#1f1f1f'
    )
    // item-title is needed to prevent a warning in the console b/c it is passed through with the v-slot:item
    v-autocomplete(
        v-model='milSymbolMatched'
        autofocus
        no-filter
        v-model:search='textPrompt'
        :items='iconCodeData'
        item-title='sidc.underlying'
        return-object
        label='Enter text prompt for icon here'
        hint='i.e. something like "sp en arty" or "icbm"'
        :loading='loadingIconCodeData'
        density="comfortable"
        :menu-props='{ maxHeight: 400 }'
    )
        template(v-slot:selection='{ item }')
            // TODO I would like to ideally be able to keep the text prompt in the input field after a selection is made,
            //    so that it can be edited further.  This displays the tokens matched but you can't edit them.
            v-list-item
                template(v-slot:prepend)
                    MilSymbol(
                        :mil-symbol='milSymbolMatchedToMilSymbolDTO(item.raw)'
                        :size='45 * milSymbolMatchedToShrinkFraction(item.raw)'
                        outlineColor='#1f1f1f'
                    )
                template(v-slot:title='{ title }')
                    v-list-item-title {{ getVAutocompleteTextAfterSelectionMade(item.raw) }}
        template(v-slot:item='{ props, item }')
            v-list-item(v-bind='props')
                template(v-slot:prepend)
                    MilSymbol(
                        :mil-symbol='milSymbolMatchedToMilSymbolDTO(item.raw)'
                        :size='60 * milSymbolMatchedToShrinkFraction(item.raw)'
                        outlineColor='#1f1f1f'
                    )
                template(v-slot:title='{ title }')
                    v-list-item-title
                        ChipMatchTokens(
                            v-for='token in item.raw.tokensUnmatchedMilSymbol'
                            :token='token'
                            color='grey'
                        )
                        ChipMatchTokens(
                            v-for='token in item.raw.tokensUnmatchedTextPrompt'
                            :token='token'
                            color='red'
                        )
                        ChipMatchTokens(
                            v-for='token in item.raw.tokensMatched'
                            :token='token'
                            color='green'
                        )
</template>

<script lang="ts">
import ValidatedTextField from '@/common/Validation/ValidatedTextField.vue';
import MilSymbol from '@/common/MilSymbol/MilSymbol.vue';
import MilSymbolDTO from '@/models/MilSymbol';
import * as _ from 'lodash';
import { alertException } from '@/util/errors';
import { queries } from '@/common/MilSymbol/mil-symbol.gql';
import {
    MilSymbolTextPromptMatch,
    MilSymbolTextPromptMatchDTO,
} from '@/common/MilSymbol/MilSymbolTextPromptMatch';
import { Sidc } from '@/common/MilSymbol/Sidc';
import { defineComponent } from 'vue';
import ChipMatchTokens from '@/common/MilSymbol/ChipMatchTokens.vue';

class MilSymbolShrinkFactorDeterminer {
    private static readonly extraVerticalMultipleAroundMainBorderOfIconForTaskForce = 0.3;
    private readonly sidc: Sidc;

    constructor(sidc: Sidc) {
        this.sidc = sidc;
    }

    private extraVerticalMultipleAroundMainBorderOfIcon(): number {
        const headquartersTaskForceDummy = this.sidc.substring(7, 8);
        const isTaskForce = ['4', '5', '6', '7'].includes(headquartersTaskForceDummy);
        const isHeadquarters = ['2', '3', '6', '7'].includes(headquartersTaskForceDummy);
        const isDummy = ['1', '3', '5', '7'].includes(headquartersTaskForceDummy);

        const v =
            MilSymbolShrinkFactorDeterminer.extraVerticalMultipleAroundMainBorderOfIconForTaskForce;
        const extraVerticalAbove = isDummy ? 2 * v : isTaskForce ? v : 0;
        const extraVerticalBelow = isHeadquarters ? v : 0;

        return extraVerticalAbove + extraVerticalBelow;
    }

    /**
     * The factor by which the icon should be shrunk so that the height of the icon is the same as if it were
     * a task force icon.  However, the icon is never expanded, so if the icon is shorter than an icon with a
     * task force then 1 is returned.
     */
    shrinkFactorSoSameHeightAsWithTaskForceAndNoShrinking() {
        const shrinkFactor =
            (1 +
                MilSymbolShrinkFactorDeterminer.extraVerticalMultipleAroundMainBorderOfIconForTaskForce) /
            (1 + this.extraVerticalMultipleAroundMainBorderOfIcon());
        return Math.min(shrinkFactor, 1);
    }
}

export default defineComponent({
    name: 'MilSymbolTextPromptDropdown',
    components: {
        ChipMatchTokens,
        MilSymbol,
        ValidatedTextField,
    },
    emits: ['mil-symbol-dto-selected'],
    props: {
        showSelection: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            iconCodeData: [] as MilSymbolTextPromptMatch[],
            milSymbolMatched: null as MilSymbolTextPromptMatch,
            loadingIconCodeData: false,
            textPrompt: '',
        };
    },
    computed: {
        milSymbolSelected() {
            return this.milSymbolMatchedToMilSymbolDTO(this.milSymbolMatched);
        },
    },
    mounted() {
        this.updateIconCodeData();
    },
    methods: {
        getVAutocompleteTextAfterSelectionMade(milSymbolMatched: MilSymbolTextPromptMatch): string {
            // return this.textPrompt;

            // Switch to this to return all the tokens expanded into a single string.
            return milSymbolMatched.getTokensMilSymbolAsString();
        },
        milSymbolMatchedToMilSymbolDTO(
            milSymbolMatched: MilSymbolTextPromptMatch | null,
        ): MilSymbolDTO {
            if (!milSymbolMatched) {
                return {
                    code: '30031000001800000000',
                    specialHeadquarters: '',
                };
            }
            return milSymbolMatched.getMilSymbolDTO();
        },
        milSymbolMatchedToShrinkFraction(
            milSymbolMatched: MilSymbolTextPromptMatch | null,
        ): number {
            if (!milSymbolMatched) {
                return 1;
            }
            const shrinkFactorDeterminer = new MilSymbolShrinkFactorDeterminer(
                milSymbolMatched.sidc,
            );
            return shrinkFactorDeterminer.shrinkFactorSoSameHeightAsWithTaskForceAndNoShrinking();
        },
        updateIconCodeData: _.debounce(async function () {
            // We want to return the MRU list if the text prompt is empty,
            // so we always query the backend, even if this.textPrompt is empty.
            this.loadingIconCodeData = true;
            const textPrompt = this.textPrompt || '';
            try {
                if (!(typeof textPrompt === 'string')) {
                    throw new Error(
                        `Expected textPrompt to be a string, but got ${typeof textPrompt}.`,
                    );
                }
                const response = await this.$apollo.query({
                    query: queries.getIconCodeGenerationDataForTextPrompt,
                    variables: {
                        textPrompt: textPrompt,
                    },
                });
                if (!response) {
                    return;
                }
                this.iconCodeData = this.iconCodeDataWithNamedHeadquartersAddedIfApplicable(
                    response.data.iconCodeGenerationData,
                );
            } catch (e) {
                alertException(
                    e,
                    `Failed to load iconCodeData in common\\MilSymbol\\MilSymbolTextPromptDropdown.vue: ${textPrompt}`,
                );
            } finally {
                this.loadingIconCodeData = false;
            }
        }, 500),
        iconCodeDataWithNamedHeadquartersAddedIfApplicable(
            iconCodeDataDTO: MilSymbolTextPromptMatchDTO[],
        ): MilSymbolTextPromptMatch[] {
            const newIconCodeData: MilSymbolTextPromptMatch[] = [];
            for (const iconCodeDatumDTO of iconCodeDataDTO) {
                const iconCodeDatum = new MilSymbolTextPromptMatch(iconCodeDatumDTO);
                newIconCodeData.push(iconCodeDatum);
                if (iconCodeDatum.isACandidateForBeingANamedHeadquarters()) {
                    newIconCodeData.push(iconCodeDatum.convertedToNamedHeadquarters());
                }
            }
            return newIconCodeData;
        },
    },
    watch: {
        textPrompt() {
            this.updateIconCodeData();
        },
        milSymbolMatched(newValue: MilSymbolTextPromptMatch) {
            const milSymbolDTO = this.milSymbolMatchedToMilSymbolDTO(newValue);
            this.$emit('mil-symbol-dto-selected', milSymbolDTO);
        },
    },
});
</script>

<style scoped>
.container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
}

.mil-symbol-container {
    margin: -0px;
}
</style>
